import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  ADD_UPDATE_COMPANY_PRODUCT,
  ADD_UPDATE_TRAINING_VIDEO,
  Get_Expert_Video,
  isSchemaMissingError,
  trainingApiAvailability,
} from "../../../../services/userAPI";

const inputClass =
  "h-10 w-full rounded-[8px] border border-[#E3E3E3] bg-white px-3 text-sm outline-none placeholder-[#B5B5B5] focus:border-[#B5B5B5]";

const LANGUAGES = [
  { id: "english", name: "English" },
  { id: "hindi", name: "Hindi" },
  { id: "telugu", name: "Telugu" },
  { id: "tamil", name: "Tamil" },
  { id: "kannada", name: "Kannada" },
  { id: "marathi", name: "Marathi" },
  { id: "gujarati", name: "Gujarati" },
  { id: "bengali", name: "Bengali" },
];

const AddCourseVideo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editing = location.state?.editing || null;
  const courseId = location.state?.course?.id;
  const isBusinessVideo = location.state?.context === "business";

  const { data: languagesData } = useQuery(Get_Expert_Video);
  const [addUpdateTrainingVideo] = useMutation(ADD_UPDATE_TRAINING_VIDEO);
  const [addUpdateCompanyProduct] = useMutation(ADD_UPDATE_COMPANY_PRODUCT);
  const languages = languagesData?.mdLanguages || LANGUAGES;

  const ROLE_ID = location.state?.roleId ? parseInt(location.state.roleId) : null;
  const ACCOUNT_ID = location.state?.accountId ? parseInt(location.state.accountId) : null;

  const [formData, setFormData] = useState({
    videoCode: editing?.videoCode || "",
    videoTitle: editing?.title || "",
    language: editing?.languageId ?? editing?.language ?? "",
    videoDuration: editing?.duration?.replace(" Min", "") || "",
    videoDescription: editing?.description || "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Thumbnail URL is always derived from the video code — field is read-only
  const thumbnailUrl = formData.videoCode.trim()
    ? `https://img.youtube.com/vi/${formData.videoCode.trim()}/0.jpg`
    : "";

  const handleDurationChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 4);
    if (value.length >= 3) value = value.slice(0, 2) + ":" + value.slice(2);
    setField("videoDuration", value);
  };

  const validateForm = () => {
    const newErrors = {};
    const code = formData.videoCode.trim();
    if (!code) newErrors.videoCode = "Video code is required";
    else if (code.length !== 11) newErrors.videoCode = "YouTube code must be exactly 11 characters";
    if (!formData.videoTitle.trim()) newErrors.videoTitle = "Video title is required";
    if (!formData.language) newErrors.language = "Language is required";
    if (!/^([0-5]?[0-9]):[0-5][0-9]$/.test(formData.videoDuration))
      newErrors.videoDuration = "Enter valid duration: MM:SS";
    if (!formData.videoDescription.trim())
      newErrors.videoDescription = "Video description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    // Business Understanding videos are company products — getCompanyProducts
    // refetches on the list page after save
    if (isBusinessVideo) {
      try {
        const res = await addUpdateCompanyProduct({
          variables: {
            input: {
              accountId: ACCOUNT_ID,
              roleId: ROLE_ID,
              productName: formData.videoTitle.trim(),
              title: formData.videoTitle.trim(),
              shortDescription: formData.videoDescription.trim(),
              publishDate: new Date().toISOString().split("T")[0],
              expiryDate: null,
              description: formData.videoDescription.trim(),
              content: {
                id: editing?.contentId ?? null,
                title: formData.videoTitle.trim(),
                languageId: parseInt(formData.language),
                commitmentNeeded: false,
                description: formData.videoDescription.trim(),
                code: formData.videoCode.trim(),
                thumbnailUrl,
                lengthMins: formData.videoDuration,
                category: "PRODUCT",
                contentTags: ["video", "update"],
              },
              ...(editing?.id && editing.id <= 2147483647
                ? { id: editing.id }
                : {}),
            },
          },
        });
        if (res?.data?.addUpdateCompanyProduct?.success === false) {
          throw new Error(res.data.addUpdateCompanyProduct.message || "Save failed");
        }
        message.success(editing ? "Video updated successfully!" : "Video added successfully!");
        setIsSubmitting(false);
        // Back to Business Understanding — getCompanyProducts refetches there
        navigate(-1);
      } catch (err) {
        message.error("Save failed: " + err.message);
        setIsSubmitting(false);
      }
      return;
    }

    if (!courseId || !trainingApiAvailability.addUpdateTrainingVideo) {
      message.success(editing ? "Video updated successfully!" : "Video added successfully!");
      setIsSubmitting(false);
      navigate(-1);
      return;
    }

    try {
      const result = await addUpdateTrainingVideo({
        variables: {
          input: {
            courseId,
            content: {
              title: formData.videoTitle.trim(),
              languageId: parseInt(formData.language),
              commitmentNeeded: false,
              description: formData.videoDescription.trim(),
              code: formData.videoCode.trim(),
              thumbnailUrl,
              lengthMins: formData.videoDuration,
              category: "Training",
              contentTags: ["Training"],
            },
            // Only send a real backend ID — timestamps from static entries overflow Int
            ...(editing?.id && editing.id <= 2147483647 ? { id: editing.id } : {}),
          },
        },
      });
      if (result?.data?.addUpdateTrainingVideo?.success === false) {
        throw new Error(result.data.addUpdateTrainingVideo.message || "Save failed");
      }
      message.success(editing ? "Video updated successfully!" : "Video added successfully!");
      setIsSubmitting(false);
      // Back to the course detail page — getCourseDetails refetches there
      navigate(-1);
    } catch (err) {
      if (isSchemaMissingError(err)) {
        trainingApiAvailability.addUpdateTrainingVideo = false;
        message.success(editing ? "Video updated successfully!" : "Video added successfully!");
        setIsSubmitting(false);
        navigate(-1);
        return;
      }
      message.error("Save failed: " + err.message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen px-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 font-['Inter'] text-[13px] font-medium text-[#AD0101] transition-colors hover:text-[#8C0101]"
      >
        <ArrowLeft size={14} strokeWidth={2.5} /> Back
      </button>

      <h1 className="mt-3 font-['Inter'] text-[20px] font-semibold leading-[100%] text-[#1F2020]">
        {isBusinessVideo
          ? editing
            ? "Edit Video"
            : "Add Video"
          : editing
            ? "Edit Training Module"
            : "Add Training Module"}
      </h1>

      {/* Video Details */}
      <div className="mt-5 rounded-[16px] border border-[#EDEDED] bg-white p-6">
        <h2 className="font-['Inter'] text-[20px] font-semibold leading-[100%] text-[#1F2020]">
          Video Details
        </h2>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
              Video Code <span className="text-[#AD0101]">*</span>
            </label>
            <input
              type="text"
              value={formData.videoCode}
              onChange={(e) => setField("videoCode", e.target.value)}
              className={inputClass}
              placeholder="Enter Video Code"
            />
            {errors.videoCode && (
              <p className="mt-1 text-xs text-[#AD0101]">{errors.videoCode}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
              Video Title <span className="text-[#AD0101]">*</span>
            </label>
            <input
              type="text"
              value={formData.videoTitle}
              onChange={(e) => setField("videoTitle", e.target.value)}
              className={inputClass}
              placeholder="Enter Video Title"
            />
            {errors.videoTitle && (
              <p className="mt-1 text-xs text-[#AD0101]">{errors.videoTitle}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
              Video Thumbnail Code <span className="text-[#AD0101]">*</span>
            </label>
            <input
              type="text"
              value={thumbnailUrl}
              disabled
              readOnly
              className={`${inputClass} cursor-not-allowed bg-[#F5F5F5] text-[#999999]`}
              placeholder="https://img.youtube.com/vi/..."
            />
          </div>

          <div>
            <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
              Video Language <span className="text-[#AD0101]">*</span>
            </label>
            <select
              value={formData.language}
              onChange={(e) => setField("language", e.target.value)}
              className={`${inputClass} ${!formData.language ? "text-[#B5B5B5]" : ""}`}
            >
              <option value="">Select Language</option>
              {languages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.name}
                </option>
              ))}
            </select>
            {errors.language && (
              <p className="mt-1 text-xs text-[#AD0101]">{errors.language}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
              Video Duration <span className="text-[#AD0101]">*</span>
            </label>
            <input
              type="text"
              value={formData.videoDuration}
              onChange={handleDurationChange}
              className={inputClass}
              placeholder="MM:SS"
            />
            {errors.videoDuration && (
              <p className="mt-1 text-xs text-[#AD0101]">{errors.videoDuration}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1 block font-['Inter'] text-[14px] font-medium leading-[20px] text-[#0A0A0A]">
            Video Description <span className="text-[#AD0101]">*</span>
          </label>
          <textarea
            value={formData.videoDescription}
            onChange={(e) => setField("videoDescription", e.target.value)}
            rows={4}
            className="w-full resize-none rounded-[8px] border border-[#E3E3E3] bg-white px-3 py-2 text-sm outline-none placeholder-[#B5B5B5] focus:border-[#B5B5B5]"
            placeholder="Enter Video Description"
          />
          {errors.videoDescription && (
            <p className="mt-1 text-xs text-[#AD0101]">{errors.videoDescription}</p>
          )}
        </div>
      </div>

      {/* Footer buttons */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-[8px] border border-[#E3E3E3] bg-white px-6 py-2 font-['Inter'] text-[14px] font-medium text-[#1F2020] transition-colors hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-[8px] bg-[#AD0101] px-6 py-2 font-['Inter'] text-[14px] font-semibold text-white transition-colors hover:bg-[#8C0101] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default AddCourseVideo;
